import { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import {
  useCreateFreshStrawDonorMutation,
  useCreateFreshStrawOutsideEmbryoMutation,
  useCreateFreshStrawSireMutation,
} from '@/app/services/freshStrawRest';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface FreshStrawOutsideEmbryoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string;
  siteId: string;
  stopNumber: number;
  transferDate: string;
  onCreated?: () => void;
}

type SubMode = null | 'donor' | 'sire';

const EMBRYO_TYPE_OPTIONS = [
  { value: '', label: '— pick type —' },
  { value: '100000000', label: 'IVF' },
  { value: '100000001', label: 'ET' },
];

const EMBRYO_SEX_OPTIONS = [
  { value: '', label: '— pick sex —' },
  { value: '100000000', label: 'Female-sexed' },
  { value: '100000001', label: 'Male-sexed' },
  { value: '100000002', label: 'Conventional' },
];

export function FreshStrawOutsideEmbryoDialog({
  open,
  onOpenChange,
  accountId,
  siteId,
  stopNumber,
  transferDate,
  onCreated,
}: FreshStrawOutsideEmbryoDialogProps) {
  const dispatch = useDispatch();

  const [donorId, setDonorId] = useState('');
  const [sireId, setSireId] = useState('');
  const [embryoType, setEmbryoType] = useState('');
  const [embryoSex, setEmbryoSex] = useState('');
  const [stage, setStage] = useState('');
  const [grade, setGrade] = useState('');
  const [tag, setTag] = useState('');
  const [straw, setStraw] = useState('');
  const [cane, setCane] = useState('');
  const [comment, setComment] = useState('');

  const [subMode, setSubMode] = useState<SubMode>(null);
  const [newDonorName, setNewDonorName] = useState('');
  const [newSireName, setNewSireName] = useState('');

  const [createEmbryo, createState] =
    useCreateFreshStrawOutsideEmbryoMutation();
  const [createDonor, donorState] = useCreateFreshStrawDonorMutation();
  const [createSire, sireState] = useCreateFreshStrawSireMutation();

  useEffect(() => {
    if (!open) {
      setDonorId('');
      setSireId('');
      setEmbryoType('');
      setEmbryoSex('');
      setStage('');
      setGrade('');
      setTag('');
      setStraw('');
      setCane('');
      setComment('');
      setSubMode(null);
      setNewDonorName('');
      setNewSireName('');
    }
  }, [open]);

  const handleCreateDonor = async () => {
    if (!newDonorName.trim()) return;
    try {
      const result = await createDonor({
        name: newDonorName.trim(),
        ownerId: accountId,
      }).unwrap();
      setDonorId(result.id);
      setSubMode(null);
      setNewDonorName('');
    } catch (err) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Could not create donor',
          detail: extractMessage(err),
          life: ToastLife.long,
        }),
      );
    }
  };

  const handleCreateSire = async () => {
    if (!newSireName.trim()) return;
    try {
      const result = await createSire({
        sire: newSireName.trim(),
        ownerId: accountId,
      }).unwrap();
      setSireId(result.id);
      setSubMode(null);
      setNewSireName('');
    } catch (err) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Could not create sire',
          detail: extractMessage(err),
          life: ToastLife.long,
        }),
      );
    }
  };

  const canSubmit =
    !!donorId &&
    !!sireId &&
    !!accountId &&
    !!siteId &&
    stopNumber > 0 &&
    !!transferDate;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await createEmbryo({
        accountId,
        donorId,
        sireId,
        siteId,
        stopNumber,
        transferDate,
        embryoType: embryoType ? Number(embryoType) : undefined,
        embryoSex: embryoSex ? Number(embryoSex) : undefined,
        stage: stage ? Number(stage) : undefined,
        grade: grade ? Number(grade) : undefined,
        tag: tag || undefined,
        straw: straw || undefined,
        cane: cane || undefined,
        comment: comment || undefined,
      }).unwrap();
      dispatch(
        addToast({
          severity: 'success',
          summary: 'Outside embryo added',
          detail: 'The embryo was created and attached to this stop.',
          life: ToastLife.short,
        }),
      );
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Could not add outside embryo',
          detail: extractMessage(err),
          life: ToastLife.long,
        }),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add outside embryo</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="grid gap-3 text-sm md:grid-cols-2">
            <Field label="Donor ID (GUID)">
              <div className="flex gap-1">
                <input
                  type="text"
                  value={donorId}
                  onChange={(e) => setDonorId(e.target.value)}
                  placeholder="GUID"
                  className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
                />
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() =>
                    setSubMode((m) => (m === 'donor' ? null : 'donor'))
                  }
                >
                  New
                </Button>
              </div>
            </Field>
            <Field label="Sire ID (GUID)">
              <div className="flex gap-1">
                <input
                  type="text"
                  value={sireId}
                  onChange={(e) => setSireId(e.target.value)}
                  placeholder="GUID"
                  className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
                />
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() =>
                    setSubMode((m) => (m === 'sire' ? null : 'sire'))
                  }
                >
                  New
                </Button>
              </div>
            </Field>

            {subMode === 'donor' && (
              <div className="flex items-end gap-2 rounded border border-blue-200 bg-blue-50 p-2 md:col-span-2">
                <label className="flex flex-1 flex-col gap-1">
                  <span className="text-xs font-medium text-gray-600">
                    New donor name
                  </span>
                  <input
                    type="text"
                    value={newDonorName}
                    onChange={(e) => setNewDonorName(e.target.value)}
                    className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                  />
                </label>
                <Button
                  size="sm"
                  onClick={handleCreateDonor}
                  disabled={!newDonorName.trim() || donorState.isLoading}
                >
                  {donorState.isLoading ? 'Creating…' : 'Create donor'}
                </Button>
              </div>
            )}

            {subMode === 'sire' && (
              <div className="flex items-end gap-2 rounded border border-blue-200 bg-blue-50 p-2 md:col-span-2">
                <label className="flex flex-1 flex-col gap-1">
                  <span className="text-xs font-medium text-gray-600">
                    New sire name
                  </span>
                  <input
                    type="text"
                    value={newSireName}
                    onChange={(e) => setNewSireName(e.target.value)}
                    className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                  />
                </label>
                <Button
                  size="sm"
                  onClick={handleCreateSire}
                  disabled={!newSireName.trim() || sireState.isLoading}
                >
                  {sireState.isLoading ? 'Creating…' : 'Create sire'}
                </Button>
              </div>
            )}

            <Field label="Embryo type">
              <select
                value={embryoType}
                onChange={(e) => setEmbryoType(e.target.value)}
                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
              >
                {EMBRYO_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Embryo sex">
              <select
                value={embryoSex}
                onChange={(e) => setEmbryoSex(e.target.value)}
                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
              >
                {EMBRYO_SEX_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Stage">
              <input
                type="number"
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
              />
            </Field>
            <Field label="Grade">
              <input
                type="number"
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
              />
            </Field>
            <Field label="Tag">
              <input
                type="text"
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
              />
            </Field>
            <Field label="Straw">
              <input
                type="text"
                value={straw}
                onChange={(e) => setStraw(e.target.value)}
                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
              />
            </Field>
            <Field label="Cane" className="md:col-span-2">
              <input
                type="text"
                value={cane}
                onChange={(e) => setCane(e.target.value)}
                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
              />
            </Field>
            <Field label="Comment" className="md:col-span-2">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={2}
                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
              />
            </Field>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            Account, site, stop, and transfer date come from the current filter
            panel.
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createState.isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || createState.isLoading}
          >
            {createState.isLoading ? 'Adding…' : 'Add embryo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={`flex flex-col gap-1 ${className ?? ''}`}>
      <span className="text-xs font-medium text-gray-600">{label}</span>
      {children}
    </label>
  );
}

function extractMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'data' in err) {
    const data = (err as { data?: { message?: string } }).data;
    if (data?.message) return data.message;
  }
  return 'Request failed.';
}
